/**
 * ONB-063 (finding F16) — the one viewport-height class every full-page
 * onboarding / upgrade surface uses.
 *
 * `svh` (the *smallest* viewport height — the one that assumes every mobile
 * browser toolbar is expanded) as the floor, upgraded to `dvh` where the
 * browser supports it.
 *
 * Not `min-h-screen`, which resolves to the legacy `vh` unit: on mobile
 * Safari and Chrome `vh` is the LARGEST viewport height, so such an
 * element's bottom sits underneath the browser toolbar. That is the real,
 * logged defect on mobile signup — the submit button of a centred card
 * ending up behind the URL bar.
 *
 * The `supports-[…]` variant rather than two bare `min-h-*` utilities
 * because both compile to the same `min-height` property and Tailwind does
 * not guarantee source order between them — a feature query is
 * deterministic. `svh` is quoted as the fallback and `dvh` inside the query
 * so a browser that understands neither still gets a sane `min-height`.
 *
 * First written for `signup-split-shell.tsx` (ONB-062), which still
 * re-exports it under the same name so its existing importers are unchanged;
 * lifted here when `OnboardingCardShell` needed the same thing and a second
 * copy of the string would have been the start of the drift.
 */
export const VIEWPORT_HEIGHT_CLASS = "min-h-svh supports-[height:100dvh]:min-h-dvh";

/**
 * Bottom padding for a surface whose last element is a CTA that can land on
 * the bottom edge of a short viewport: the ordinary padding, or the home
 * indicator / Fold chin, whichever is larger. `env()` resolves to 0 on
 * hardware with no inset, so `max()` keeps the ordinary value there.
 *
 * Distinct from `@akili/ui-responsive`'s `SAFE_BOTTOM` (0.75rem), which is
 * the inset for a fixed/sticky bar hugging the edge. A page whose content
 * ends in a button wants the page's own 1.5rem gutter as its floor, not a
 * bar's 0.75rem. Re-exported as `SIGNUP_SAFE_BOTTOM` from
 * `signup-split-shell.tsx` so both onboarding shells inset by the same
 * amount rather than by two nearly-equal amounts.
 */
export const ONBOARDING_SAFE_BOTTOM = "pb-[max(1.5rem,env(safe-area-inset-bottom))]";
